// Camera state conversion utilities

import type { 
  CameraState, 
  CameraStateEuler, 
  CameraStateQuaternion
} from '@/types/camera'
import { 
  isEulerCamera, 
  isQuaternionCamera 
} from '@/types/camera'
import { 
  quaternionFromEuler, 
  quaternionToEuler, 
  IDENTITY_QUATERNION,
  type Quaternion 
} from './quaternion'

// Convert any camera state to quaternion format
export function toQuaternionCamera(camera: CameraState): CameraStateQuaternion {
  if (isQuaternionCamera(camera)) {
    return camera
  }
  
  // Convert from Euler to quaternion
  const quaternion = quaternionFromEuler(
    camera.rotation.rx,
    camera.rotation.ry,
    camera.rotation.rz
  )
  
  return {
    position: { ...camera.position },
    quaternion
  }
}

// Convert any camera state to Euler format (for UI display)
export function toEulerCamera(camera: CameraState): CameraStateEuler {
  if (isEulerCamera(camera)) {
    return camera
  }
  
  // Convert from quaternion to Euler
  const [rx, ry, rz] = quaternionToEuler(camera.quaternion)
  
  return {
    position: { ...camera.position },
    rotation: { rx, ry, rz }
  }
}

// Convert camera state to position and rotation arrays (for renderer)
export function cameraStateToArrays(camera: CameraState | undefined): {
  position: [number, number, number]
  rotation: [number, number, number]
} {
  if (!camera) {
    return {
      position: [0, 0, 5],
      rotation: [0, 0, 0]
    }
  }
  
  const eulerCamera = toEulerCamera(camera)
  return {
    position: [eulerCamera.position.x, eulerCamera.position.y, eulerCamera.position.z],
    rotation: [eulerCamera.rotation.rx, eulerCamera.rotation.ry, eulerCamera.rotation.rz]
  }
}

// Convert camera state to position and quaternion (for quaternion-based renderer)
export function cameraStateToQuaternion(camera: CameraState | undefined): {
  position: [number, number, number]
  quaternion: Quaternion
} {
  if (!camera) {
    return {
      position: [0, 0, 5],
      quaternion: IDENTITY_QUATERNION
    }
  }
  
  const quatCamera = toQuaternionCamera(camera)
  return {
    position: [quatCamera.position.x, quatCamera.position.y, quatCamera.position.z],
    quaternion: quatCamera.quaternion
  }
}

// Create camera state from position and rotation arrays
export function arraysToCameraState(
  position: [number, number, number],
  rotation: [number, number, number]
): CameraStateQuaternion {
  const quaternion = quaternionFromEuler(rotation[0], rotation[1], rotation[2])
  
  return {
    position: { x: position[0], y: position[1], z: position[2] },
    quaternion
  }
}

// Create camera state from position and quaternion
export function quaternionToCameraState(
  position: [number, number, number],
  quaternion: Quaternion
): CameraStateQuaternion {
  return {
    position: { x: position[0], y: position[1], z: position[2] },
    quaternion
  }
}

// Convert quaternion to Euler angles in degrees for UI display
export function quaternionToEulerDegrees(quaternion: Quaternion): { rx: number; ry: number; rz: number } {
  const [rx, ry, rz] = quaternionToEuler(quaternion)
  return {
    rx: rx * (180 / Math.PI),
    ry: ry * (180 / Math.PI),
    rz: rz * (180 / Math.PI)
  }
}

// Convert Euler angles in degrees to quaternion for UI input
export function eulerDegreesToQuaternion(rx: number, ry: number, rz: number): Quaternion {
  return quaternionFromEuler(
    rx * (Math.PI / 180),
    ry * (Math.PI / 180),
    rz * (Math.PI / 180)
  )
}

// Migrate old camera data to new format
export function migrateCameraState(camera: unknown): CameraState {
  const cam = camera as any
  
  // If it already has quaternion, it's already migrated
  if (cam?.quaternion) {
    return cam as CameraStateQuaternion
  }
  
  // If it has rotation, convert it
  if (cam?.rotation) {
    return toQuaternionCamera(cam as CameraStateEuler)
  }
  
  // Default camera state
  return {
    position: { x: 0, y: 0, z: 5 },
    quaternion: IDENTITY_QUATERNION
  }
}